import _ from 'lodash';
import { UAParser } from 'ua-parser-js';

import { logsApi } from 'src/api/logsApi';
import buildDate from 'src/build_date';
import { APP_BUILD } from 'src/constants';
import { Settings, SettingsKeys } from 'src/contexts/SettingsContext';
import checkPwa from 'src/utils/checkPwa';

const INSPECTION_USER_IDS = [21516, 23725, 18377, 17336, 27314, 26965];

const LOG_LEVELS = {
  debug: 'debug',
  warn: 'warn',
  error: 'error',
  info: 'info',
};

const userAgent = new UAParser().getResult();
const systemData = {
  browser: `${_.get(userAgent, 'browser.name')} ${_.get(userAgent, 'browser.version')}`,
  os: `${_.get(userAgent, 'os.name')} ${_.get(userAgent, 'os.version')}`,
  device: _.values(userAgent.device).some((x) => x !== undefined)
    ? userAgent.device
    : undefined,
  is_installed: checkPwa(),
};

const getHTMLDate = () =>
  document.querySelector("meta[name='date']")?.getAttribute('content');

class Logger {
  settings = undefined;
  defaultInfo = undefined;
  logQueue = [];
  flushInterval = undefined;

  constructor(settings) {
    this.settings = settings;
    this.defaultInfo = settings.get(SettingsKeys.loggerDefaultInfo);

    this.flushInterval = setInterval(() => this.flush(), 60_000);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden || document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  setDefaultInfo(info) {
    this.defaultInfo = info;
    this.settings.set(SettingsKeys.loggerDefaultInfo, info);
  }

  clear() {
    this.setDefaultInfo(null);
  }

  enqueue(log, skipIgnoreList = false) {
    const ignoreList = [
      '[ErrorFallback] Loading chunk',
      'There is no account linked to this',
      'Initialization took too long! If you expect long running init',
      'Token expired',
      '[ProcessingHandler] error: requestId undefined error {}',
      '[ServerRecognitionContext] Socket error: {}',
      'text should not be empty',
      'already exists',
      'Unauthorized',
      'email address is in use',
      'Streaming already active',
    ];

    if (
      !skipIgnoreList &&
      ignoreList.some((ignore) => log.message.includes(ignore))
    ) {
      return;
    }

    const data = {
      ...(this.defaultInfo || {}),
      ...(log.data || {}),
      location: window.location.pathname,
      app_build: APP_BUILD,
      build_date: buildDate,
      html_date: getHTMLDate(),
      ...systemData,
    };

    this.logQueue.push({
      level: log.level,
      message: log.message,
      data,
    });

    console.log(`[${log.level.toUpperCase()}]`, log.message, data);
  }

  async flush() {
    if (this.logQueue.length === 0) return;

    const batch = [...this.logQueue];
    this.logQueue = [];

    try {
      await logsApi.sendBatch(batch);
    } catch (err) {
      console.error('Failed to send log batch', err);
      // Requeue the failed logs
      this.logQueue.unshift(...batch);
    }
  }

  error(message, additionalInfo = null) {
    this.enqueue({ level: LOG_LEVELS.error, message, data: additionalInfo });
  }

  info(message, additionalInfo = null) {
    this.enqueue({ level: LOG_LEVELS.info, message, data: additionalInfo });
  }

  debug(message, additionalInfo = null) {
    this.enqueue({ level: LOG_LEVELS.debug, message, data: additionalInfo });
  }

  warn(message, additionalInfo = null) {
    this.enqueue({ level: LOG_LEVELS.warn, message, data: additionalInfo });
  }

  inspection(message, additionalInfo = null) {
    const userId = additionalInfo?.user_id || this.defaultInfo?.user_id;
    if (!INSPECTION_USER_IDS.includes(userId)) {
      return;
    }

    this.enqueue(
      { level: LOG_LEVELS.info, message, data: additionalInfo },
      true,
    );
  }
}

const settings = Settings();
const logger = new Logger(settings);

export default logger;
